// ================================================================
// AuthGate — Vollbild-Login/Registrierung (vor dem App-Boot), Boss Pilot-Marke.
// ================================================================
// Wird vor dem init der App gezeigt. Nach Erfolg → onSuccess() bootet die App.
// Zusätzlich zu E-Mail/Passwort: SSO-Login mit Google/Microsoft (nur bereits
// eingeladene Konten — der Backend-Flow lehnt unbekannte E-Mails ab).

import './auth-gate.css';
import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { login, register, setSession, requestPasswordReset, fetchMe, verifyMfaLogin, confirmDeviceLogin } from '../../core/auth.js';
import { takeSignOutReason } from '../../core/signedOut.js';
import { apiRequest } from '../../core/apiClient.js';
import { authShell, bindLangSelect, redrawKeepingInput } from './authShell.js';
import { authField, authError, authSubmit } from './authFields.js';

let rootEl = null;
let onSuccessCb = null;
let mode = 'login'; // 'login' | 'register' | 'forgot'
let busy = false;
let showPw = false;
let errorMsg = '';
let forgotSent = false; // «Link angefordert»-Bestätigung statt Formular
// Zweiter Faktor: Passwort war richtig, jetzt fehlt nur noch der Code.
// Der mfaToken lebt 5 Minuten und liegt BEWUSST nur hier im Modul-Zustand —
// nicht in localStorage: er ist ein halber Login, kein Sitzungsnachweis.
let mfaToken = '';
// Geräte-Rückfrage: Passwort (und ggf. Code) stimmen, aber das Konto läuft
// bereits auf einem anderen Gerät. Gleiches Muster wie mfaToken — ein halber
// Login gehört NICHT in localStorage.
let deviceToken = '';
let deviceInfo = null; // { activeDevices, maxSessions, suggestedDeviceName }
// Warum die Anmeldemaske gerade zu sehen ist. Wird EINMAL beim Öffnen gelesen
// (takeSignOutReason löscht dabei) — sonst klebte der Hinweis an jeder
// künftigen Anmeldung.
let signedOut = null;

// Provider-Glyphen (offizielle Marken-Farben) als kleine inline-SVGs.
const GOOGLE_G = `<svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true"><path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-2.8-.4-4H24v7.3h11.9c-.2 1.9-1.5 4.8-4.4 6.7l-.04.3 6.4 4.9.4.05c4-3.7 6.3-9.2 6.3-15.6z"/><path fill="#34A853" d="M24 46c5.8 0 10.6-1.9 14.2-5.2l-6.8-5.2c-1.8 1.3-4.3 2.2-7.4 2.2-5.6 0-10.4-3.7-12.1-8.9l-.3.02-6.6 5.1-.1.3C8.2 41.1 15.5 46 24 46z"/><path fill="#FBBC05" d="M11.9 28.9c-.4-1.3-.7-2.6-.7-4s.2-2.8.6-4l-.01-.3-6.7-5.2-.2.1C3.6 18.4 3 21.1 3 24s.6 5.6 1.7 8l7.2-3.1z"/><path fill="#EA4335" d="M24 10.7c4 0 6.6 1.7 8.1 3.1l5.9-5.8C34.6 4.7 29.8 3 24 3 15.5 3 8.2 7.9 4.7 15l7.2 5.6c1.7-5.2 6.5-8.9 12.1-8.9z"/></svg>`;
const MS_SQUARES = `<svg viewBox="0 0 23 23" width="18" height="18" aria-hidden="true"><path fill="#F25022" d="M1 1h10v10H1z"/><path fill="#7FBA00" d="M12 1h10v10H12z"/><path fill="#00A4EF" d="M1 12h10v10H1z"/><path fill="#FFB900" d="M12 12h10v10H12z"/></svg>`;

export function showAuthGate(container, onSuccess) {
  rootEl = container;
  onSuccessCb = onSuccess;
  mode = 'login';
  busy = false;
  showPw = false;
  errorMsg = '';
  forgotSent = false;
  mfaToken = '';
  deviceToken = '';
  deviceInfo = null;
  signedOut = takeSignOutReason();
  document.title = t('app.title');
  draw();
}

// «Passwort vergessen» — eigene Karte statt eines dritten Zweigs im
// Login-Formular: sie hat nur ein Feld und einen anderen Ausgang.
function forgotCard() {
  if (forgotSent) {
    return `
      <div role="status" aria-atomic="true">
        <div class="auth-done">${icon('mark_email_read', 32)}</div>
        <h1 class="auth-heading">${escapeHtml(t('auth.forgot.sentTitle'))}</h1>
        <p class="auth-subtitle">${escapeHtml(t('auth.forgot.sentText'))}</p>
      </div>
      <button type="button" class="auth-submit" data-mode="login" data-auth-focus="login">
        ${escapeHtml(t('auth.forgot.back'))}
      </button>`;
  }
  return `
    <h1 class="auth-heading">${escapeHtml(t('auth.forgot.title'))}</h1>
    <p class="auth-subtitle">${escapeHtml(t('auth.forgot.subtitle'))}</p>
    <form id="auth-form" class="auth-form" novalidate aria-busy="${busy}">
      ${authField({ name: 'email', label: 'auth.email', type: 'email', autocomplete: 'email', required: true, busy, placeholder: 'auth.emailPlaceholder' })}
      ${authError(errorMsg)}
      ${authSubmit('auth.forgot.cta', busy, 'send')}
    </form>
    <p class="auth-switch">
      <button class="auth-switch-btn" type="button" data-mode="login" data-auth-focus="login" ${busy ? 'disabled' : ''}>${escapeHtml(t('auth.forgot.back'))}</button>
    </p>`;
}

// Zweiter Faktor — eigene Karte, weil hier weder E-Mail noch Passwort mehr
// hingehören: beides ist bereits geprüft. Ein Feld, ein Ausgang, plus der
// Hinweis, dass auch ein Backup-Code funktioniert (sonst steht jemand mit
// verlorenem Handy vor einer Wand, obwohl er die Codes im Portemonnaie hat).
function mfaCard() {
  return `
    <h1 class="auth-heading">${escapeHtml(t('auth.mfa.title'))}</h1>
    <p class="auth-subtitle">${escapeHtml(t('auth.mfa.subtitle'))}</p>

    <form id="auth-form" class="auth-form" novalidate aria-busy="${busy}">
      <label class="auth-field">
        <span class="auth-label">${escapeHtml(t('auth.mfa.codeLabel'))}</span>
        <span class="auth-inputwrap">
          <span class="auth-ico">${icon('lock', 20)}</span>
          <input class="auth-input" type="text" name="mfaCode" inputmode="numeric"
                 autocomplete="one-time-code" maxlength="20" autofocus
                 placeholder="${escapeHtml(t('auth.mfa.codePlaceholder'))}" />
        </span>
      </label>

      ${authError(errorMsg)}

      ${authSubmit('auth.mfa.cta', busy, 'login')}
    </form>

    <p class="auth-hint">${escapeHtml(t('auth.mfa.backupHint'))}</p>
    <p class="auth-switch">
      <button class="auth-switch-btn" type="button" data-mode="login">${escapeHtml(t('auth.mfa.cancel'))}</button>
    </p>`;
}


// ── «Ein Konto, ein Gerät» ────────────────────────────────

// Wie ein Gerät in Meldungen und Listen erscheint: Symbol + Name + wann es
// zuletzt benutzt wurde. Der Name kommt fertig vom Server (selbst vergeben
// oder aus dem User-Agent) — die Oberfläche rät hier NICHTS.
function deviceRow(d) {
  return `
    <li class="auth-device">
      <span class="auth-device-ico">${icon('devices', 20)}</span>
      <span class="auth-device-text">
        <strong>${escapeHtml(d.label || t('auth.device.unknown'))}</strong>
        <small>${escapeHtml(relativeTime(d.lastSeenAt))}</small>
      </span>
    </li>`;
}

// «vor 5 Minuten» / «gestern» — bewusst grob: auf die Sekunde genau hilft
// niemandem, und Intl.RelativeTimeFormat spricht alle 17 Sprachen von selbst.
function relativeTime(iso) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.round((then - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(document.documentElement.lang || 'de', { numeric: 'auto' });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

// Die Rückfrage VOR dem Rauswurf. Zwei Dinge muss sie leisten:
//  1. zeigen, WELCHES Gerät abgemeldet wird — damit der Nutzer erkennt, ob das
//     sein eigenes ist oder ein fremdes,
//  2. den Ausweg nennen, wenn es ein fremdes ist: Passwort ändern. Ohne diesen
//     Satz wäre die Maske eine Warnung ohne Handlungsmöglichkeit.
function deviceConfirmCard() {
  const devices = deviceInfo?.activeDevices ?? [];
  return `
    <h1 class="auth-heading">${escapeHtml(t('auth.device.title'))}</h1>
    <p class="auth-subtitle">${escapeHtml(t('auth.device.subtitle'))}</p>

    <ul class="auth-devices">${devices.map(deviceRow).join('')}</ul>

    <p class="auth-hint auth-hint--warn">${icon('info', 16)}<span>${escapeHtml(t('auth.device.onlyOne'))}</span></p>

    <form id="auth-form" class="auth-form" novalidate aria-busy="${busy}">
      <label class="auth-field">
        <span class="auth-label">${escapeHtml(t('auth.device.nameLabel'))}</span>
        <span class="auth-inputwrap">
          <span class="auth-ico">${icon('edit', 20)}</span>
          <input class="auth-input" type="text" name="deviceName" maxlength="60"
                 value="${escapeHtml(deviceInfo?.suggestedDeviceName || '')}"
                 placeholder="${escapeHtml(t('auth.device.namePlaceholder'))}" />
        </span>
      </label>

      ${authError(errorMsg)}

      ${authSubmit('auth.device.cta', busy, 'login')}
    </form>

    <p class="auth-hint">${escapeHtml(t('auth.device.notYouHint'))}</p>
    <p class="auth-switch">
      <button class="auth-switch-btn" type="button" data-mode="login">${escapeHtml(t('auth.device.cancel'))}</button>
    </p>`;
}

// Der Hinweis ÜBER dem Anmeldeformular, wenn dieses Gerät gerade rausgeworfen
// wurde. Kein eigener Bildschirm: der Nutzer soll sich ja sofort wieder
// anmelden können — ein Zwischenschritt mit «OK»-Knopf wäre nur eine Hürde.
function signedOutBanner() {
  if (!signedOut) return '';
  // Leer = das Gerät war nicht erkennbar. Dann die Meldung OHNE Namen, statt
  // «angemeldet auf «»» zu zeigen.
  const byDevice = (signedOut.byDevice || '').trim();
  let text;
  if (signedOut.code === 'AUTH_SESSION_REVOKED_OTHER_DEVICE') {
    text = byDevice
      ? t('auth.signedOut.otherDeviceNamed').replace('{device}', byDevice)
      : t('auth.signedOut.otherDevice');
  } else if (signedOut.code === 'AUTH_SESSION_REVOKED_PASSWORD_CHANGED') {
    text = t('auth.signedOut.passwordChanged');
  } else if (signedOut.code === 'AUTH_SESSION_REVOKED_MANUAL') {
    text = t('auth.signedOut.manual');
  } else {
    text = t('auth.signedOut.generic');
  }
  return `
    <div class="auth-signedout">
      <span class="auth-signedout-ico">${icon('logout', 20)}</span>
      <div>
        <strong>${escapeHtml(t('auth.signedOut.title'))}</strong>
        <p>${escapeHtml(text)}</p>
      </div>
    </div>`;
}

// Jedes Neuzeichnen läuft durch redrawKeepingInput — sonst leert eine falsche
// Passworteingabe auch die E-Mail, und das Auge leert das Passwort. Nebeneffekt,
// der hier genau richtig ist: Wer «Passwort vergessen» klickt, findet seine
// eben getippte E-Mail im Formular wieder.
function draw() {
  redrawKeepingInput(rootEl, render);
}

function render() {
  const isLogin = mode === 'login';
  if (mode === 'forgot') {
    rootEl.innerHTML = authShell(forgotCard(), { busy });
    attachHandlers();
    return;
  }

  // Zweiter Faktor und Geräte-Rückfrage sind eigene SCHRITTE derselben Anmeldung
  // — eigene Karten im selben Rahmen, kein eigenes Erscheinungsbild.
  if (mode === 'mfa') {
    rootEl.innerHTML = authShell(mfaCard(), { busy });
    attachHandlers();
    return;
  }

  if (mode === 'device') {
    rootEl.innerHTML = authShell(deviceConfirmCard(), { busy });
    attachHandlers();
    return;
  }

  rootEl.innerHTML = authShell(`
    ${isLogin ? signedOutBanner() : ''}
    <h1 class="auth-heading">${escapeHtml(t(isLogin ? 'auth.welcomeBack' : 'auth.createAccount'))}</h1>
    <p class="auth-subtitle">${escapeHtml(t(isLogin ? 'auth.loginSubtitle' : 'auth.registerSubtitle'))}</p>
    <form id="auth-form" class="auth-form" novalidate aria-busy="${busy}">
      ${!isLogin ? authField({ name: 'name', label: 'auth.name', busy, placeholder: 'auth.namePlaceholder' }) : ''}
      ${authField({ name: 'email', label: 'auth.email', type: 'email', autocomplete: 'username', required: true, busy, placeholder: 'auth.emailPlaceholder' })}
      ${authField({ name: 'password', label: 'auth.password', password: true, showPw, autocomplete: isLogin ? 'current-password' : 'new-password', hint: isLogin ? null : 'auth.passwordHint', required: true, busy, placeholder: 'auth.passwordPlaceholder' })}
      ${isLogin ? `<button type="button" class="auth-forgot" data-forgot data-auth-focus="forgot" ${busy ? 'disabled' : ''}>${escapeHtml(t('auth.forgotPassword'))}</button>` : ''}
      ${authError(errorMsg)}
      ${authSubmit(isLogin ? 'auth.loginCta' : 'auth.registerCta', busy)}
    </form>
    ${isLogin ? `
      <div class="auth-or"><span>${escapeHtml(t('auth.providerNote'))}</span></div>
      <button type="button" class="auth-sso" data-sso="google" data-auth-focus="google" ${busy ? 'disabled' : ''}>
        ${GOOGLE_G}<span>${escapeHtml(t('auth.continueGoogle'))}</span>
      </button>
      <button type="button" class="auth-sso" data-sso="microsoft" data-auth-focus="microsoft" ${busy ? 'disabled' : ''}>
        ${MS_SQUARES}<span>${escapeHtml(t('auth.continueMicrosoft'))}</span>
      </button>
      ` : ''}
    <p class="auth-switch">
      <span>${escapeHtml(t(isLogin ? 'auth.noAccount' : 'auth.haveAccount'))}</span>
      <button class="auth-switch-btn" type="button" data-mode="${isLogin ? 'register' : 'login'}" data-auth-focus="${isLogin ? 'register' : 'login'}" ${busy ? 'disabled' : ''}>
        ${escapeHtml(t(isLogin ? 'auth.createAccount' : 'auth.login'))}
      </button>
    </p>
  `, { busy });
  attachHandlers();
}

function setMode(next) {
  if (busy || next === mode) return;
  mode = next;
  errorMsg = '';
  showPw = false;
  forgotSent = false;
  // Zurück zum Anfang heisst auch: die halbe Anmeldung verfällt. Ein liegen
  // gebliebener deviceToken würde sonst beim nächsten Versuch ein fremdes
  // Gerät abmelden, ohne dass noch jemand die Rückfrage gesehen hat.
  if (next === 'login') { deviceToken = ''; deviceInfo = null; }
  draw();
  const heading = rootEl.querySelector('.auth-heading');
  heading?.setAttribute('tabindex', '-1');
  heading?.focus({ preventScroll: true });
}

function attachHandlers() {
  bindLangSelect(rootEl, draw);
  rootEl.querySelectorAll('[data-mode]').forEach((btn) => {
    btn.addEventListener('click', () => setMode(btn.dataset.mode));
  });
  rootEl.querySelector('[data-forgot]')?.addEventListener('click', () => setMode('forgot'));
  rootEl.querySelector('[data-eye]')?.addEventListener('click', () => { showPw = !showPw; draw(); });
  rootEl.querySelectorAll('[data-sso]').forEach((btn) => {
    btn.addEventListener('click', () => startSsoLogin(btn.dataset.sso));
  });
  const form = rootEl.querySelector('#auth-form');
  form?.addEventListener('submit', onSubmit);
}

async function onSubmit(e) {
  e.preventDefault();
  if (busy) return;

  const form = e.currentTarget;
  const email = (form.elements.namedItem('email')?.value || '').trim();
  const password = form.elements.namedItem('password')?.value || '';
  const name = (form.elements.namedItem('name')?.value || '').trim();
  if (email && !form.elements.namedItem('email').validity.valid) { errorMsg = { key: 'auth.errors.validation' }; draw(); return; }

  // «Passwort vergessen»: nur die Adresse, und die Antwort ist IMMER dieselbe
  // Bestätigung — auch für unbekannte Adressen (das entscheidet der Server,
  // die Oberfläche darf es nicht verraten).
  if (mode === 'forgot') {
    if (!email) { errorMsg = { key: 'auth.forgot.emailRequired' }; draw(); return; }
    busy = true; errorMsg = ''; draw();
    try {
      await requestPasswordReset(email);
      forgotSent = true;
    } catch (err) {
      errorMsg = err.status === 429 ? { key: 'auth.errors.rateLimit' } : (err.message || { key: 'auth.errors.generic' });
    }
    busy = false; draw();
    return;
  }

  // Zweiter Faktor: eigener Zweig VOR der E-Mail/Passwort-Prüfung — in dieser
  // Maske gibt es beide Felder gar nicht mehr.
  if (mode === 'mfa') {
    const code = (form.elements.namedItem('mfaCode')?.value || '').trim();
    if (!code) { errorMsg = t('auth.mfa.codeRequired'); draw(); return; }
    busy = true; errorMsg = ''; draw();
    let mfaRes;
    try {
      mfaRes = await verifyMfaLogin(mfaToken, code);
    } catch (err) {
      busy = false;
      if (err.status === 429) errorMsg = t('auth.errors.rateLimit');
      else if (err.code === 'MFA_CHALLENGE_EXPIRED') {
        // Der 5-Minuten-Token ist abgelaufen: zurück zum Passwort, sonst tippt
        // der Nutzer ewig Codes gegen einen toten Vorgang.
        mode = 'login'; mfaToken = '';
        errorMsg = t('auth.mfa.expired');
      } else errorMsg = t('auth.mfa.invalid');
      draw();
      return;
    }
    busy = false;
    mfaToken = '';
    // Code war richtig — aber das Konto läuft schon auf einem anderen Gerät:
    // noch keine Session, sondern die zweite Rückfrage.
    if (mfaRes?.deviceConfirmRequired) {
      deviceToken = mfaRes.deviceToken;
      deviceInfo = mfaRes;
      mode = 'device';
      errorMsg = '';
      draw();
      return;
    }
    mode = 'login';
    try { onSuccessCb?.(); } catch { errorMsg = t('auth.errors.generic'); draw(); }
    return;
  }

  // Geräte-Rückfrage bestätigt: JETZT erst fliegen die anderen Geräte raus.
  if (mode === 'device') {
    const deviceName = (form.elements.namedItem('deviceName')?.value || '').trim();
    busy = true; errorMsg = ''; draw();
    try {
      await confirmDeviceLogin(deviceToken, deviceName || undefined);
    } catch (err) {
      busy = false;
      if (err.status === 429) errorMsg = t('auth.errors.rateLimit');
      else if (err.code === 'AUTH_DEVICE_CHALLENGE_EXPIRED') {
        // Der 5-Minuten-Token ist tot: zurück zum Passwort. Die Liste der
        // Geräte ist dann ohnehin veraltet.
        mode = 'login'; deviceToken = ''; deviceInfo = null;
        errorMsg = t('auth.device.expired');
      } else errorMsg = err.message || t('auth.errors.generic');
      draw();
      return;
    }
    busy = false;
    deviceToken = ''; deviceInfo = null; mode = 'login';
    try { onSuccessCb?.(); } catch { errorMsg = t('auth.errors.generic'); draw(); }
    return;
  }

  if (!email || !password) { errorMsg = { key: 'auth.errors.required' }; draw(); return; }
  if (mode === 'register' && password.length < 8) { errorMsg = { key: 'auth.errors.passwordShort' }; draw(); return; }

  busy = true; errorMsg = ''; draw();
  try {
    if (mode === 'login') {
      const res = await login(email, password);
      // Konto hat einen zweiten Faktor: NICHT weiterrouten, sondern die
      // Code-Maske zeigen. `res` enthält hier keine Session.
      if (res?.mfaRequired) {
        mfaToken = res.mfaToken;
        mode = 'mfa';
        busy = false;
        errorMsg = '';
        draw();
        return;
      }
      // Konto läuft bereits auf einem anderen Gerät: erst fragen, dann
      // abmelden. `res` enthält hier ebenfalls keine Session.
      if (res?.deviceConfirmRequired) {
        deviceToken = res.deviceToken;
        deviceInfo = res;
        mode = 'device';
        busy = false;
        errorMsg = '';
        draw();
        return;
      }
    } else await register(email, password, name || undefined);
  } catch (err) {
    busy = false;
    if (err.status === 429) errorMsg = { key: 'auth.errors.rateLimit' };
    else if (err.status === 401) errorMsg = { key: 'auth.errors.invalidCredentials' };
    else if (err.status === 409) errorMsg = { key: 'auth.errors.emailExists' };
    else if (err.status === 422) errorMsg = { key: 'auth.errors.validation' };
    else errorMsg = err.message || { key: 'auth.errors.generic' };
    draw();
    return;
  }
  // Login/Registrierung ist durch — busy VOR dem Weiterrouten freigeben.
  // Vorher blieb busy für immer true: wirft das Routing (z.B. Chunk eines
  // alten Deploys nicht mehr ladbar), fror das Formular auf «bitte warten»
  // ein und nur ein Reload half. Normalfall: die App ersetzt das DOM sofort,
  // niemand sieht den Unterschied; im Fehlerfall bleibt der Knopf klickbar.
  busy = false;
  try {
    onSuccessCb?.();
  } catch {
    errorMsg = { key: 'auth.errors.generic' };
    draw();
  }
}

// SSO-Login: Popup SOFORT + SYNCHRON öffnen (iOS-Safari erlaubt window.open nur
// direkt in der Klick-Geste), dann Start-URL nachladen. Ergebnis (Session-Tokens)
// kommt normalerweise per postMessage vom Callback-Popup — ABER Google/Microsoft
// setzen auf ihren eigenen Consent-Seiten ein strenges Cross-Origin-Opener-Policy,
// das window.opener im Popup dauerhaft trennt, auch nachdem es zurück auf unsere
// Origin navigiert (reproduziert 2026-07-27: Popup schloss sich, Tab hing für immer
// auf "bitte warten"). Deshalb zusätzlich Polling auf /api/auth/sso/login-result/:code
// (ssoLoginResult.store.ts) — wer zuerst kommt (Message oder Poll), gewinnt.
async function startSsoLogin(provider) {
  if (busy) return;
  // In der nativen iOS/Android-Shell (Capacitor) gibt es keine Popups, und
  // Google lehnt OAuth in eingebetteten WebViews ab (disallowed_useragent).
  // Dort öffnet @capacitor/browser die Consent-Seite im System-Browser-Sheet
  // (SFSafariViewController); das Ergebnis kommt NUR über das Polling auf
  // /api/auth/sso/login-result/:code — postMessage gibt es dort nicht.
  const cap = window.Capacitor;
  const isNative = !!cap?.isNativePlatform?.();
  let popup = null;
  if (!isNative) {
    // explizit zentrieren (wie core/oauth.js) — ohne left/top platziert der
    // Browser das Fenster manchmal ausserhalb des sichtbaren Bereichs.
    const w = 500;
    const h = 680;
    const left = window.screen.width / 2 - w / 2;
    const top = window.screen.height / 2 - h / 2;
    popup = window.open('', 'pm_sso', `width=${w},height=${h},left=${left},top=${top}`);
    if (!popup) { errorMsg = { key: 'auth.errors.popupBlocked' }; draw(); return; }
  }

  let settled = false;
  let browserClosedListener = null;

  const finish = async (result) => {
    if (settled) return;
    settled = true;
    window.removeEventListener('message', onMessage);
    try { browserClosedListener?.remove(); } catch { /* egal */ }
    if (popup) { try { popup.close(); } catch { /* egal */ } }
    if (isNative) { try { await cap.Plugins.Browser.close(); } catch { /* Sheet evtl. schon zu */ } }
    busy = false;
    // «Ein Konto, ein Gerät» greift auch bei SSO: statt Tokens kommt die
    // Rückfrage durch. Noch KEINE Session — erst die Bestätigung im
    // Hauptfenster macht eine daraus.
    if (result?.success && result.deviceConfirmRequired) {
      deviceToken = result.deviceToken;
      deviceInfo = result;
      mode = 'device';
      errorMsg = '';
      draw();
      return;
    }
    if (result?.success && result.accessToken) {
      setSession({ accessToken: result.accessToken, refreshToken: result.refreshToken, user: result.user });
      // WICHTIG — wie beim Passwort-Login: die Login-Antwort enthält nur
      // {id, email, name} (createSession), NICHT organization/role. Ohne
      // fetchMe() routet die App mit halbem Wissen: onboardingCompletedAt fehlt →
      // der Einrichtungs-Assistent kam nochmal. Ein Reload heilte es (dort
      // läuft ensureAuthenticated → fetchMe) — genau das war das Symptom.
      try { await fetchMe(); } catch { /* Session gilt; Rest sync't später */ }
      onSuccessCb?.();
    } else {
      errorMsg = result?.error === 'no_account' ? { key: 'auth.errors.ssoNoAccount' } : (result?.error || { key: 'auth.errors.generic' });
      draw();
    }
  };

  const onMessage = (ev) => {
    if (ev.origin !== window.location.origin) return;
    const d = ev.data;
    if (!d || d.type !== 'oauth_login') return;
    finish(d);
  };
  window.addEventListener('message', onMessage);

  busy = true; errorMsg = ''; draw();
  try {
    const res = await apiRequest(`/api/auth/${provider}/login/start`);
    if (isNative) {
      // Ohne resultCode kann die native Shell das Ergebnis nie erfahren
      // (kein window.opener/postMessage im System-Browser) — sauber abbrechen.
      if (!res.resultCode) throw new Error(t('auth.errors.generic'));
      await cap.Plugins.Browser.open({ url: res.url });
      // Nutzer tippt «Fertig» im Sheet, ohne den Login abzuschliessen →
      // kurz warten (das Ergebnis kann dem Schliessen zuvorkommen), dann
      // still zurücksetzen statt 3 Minuten weiterzupollen.
      browserClosedListener = await cap.Plugins.Browser.addListener('browserFinished', () => {
        setTimeout(() => {
          if (settled) return;
          settled = true;
          window.removeEventListener('message', onMessage);
          busy = false; draw();
        }, 2000);
      });
    } else {
      popup.location.href = res.url;
    }
    if (res.resultCode) pollSsoLoginResult(res.resultCode, finish, () => settled);
  } catch (err) {
    window.removeEventListener('message', onMessage);
    try { browserClosedListener?.remove(); } catch { /* egal */ }
    if (popup) { try { popup.close(); } catch { /* egal */ } }
    busy = false;
    errorMsg = err.message || { key: 'auth.errors.generic' };
    draw();
  }
}

// Polling-Fallback: alle 1.5s bis zu 3 Minuten, genau wie core/oauth.js es für
// den Connection-Flow (dort gegen /api/connections) schon macht.
async function pollSsoLoginResult(code, finish, isSettled) {
  const deadline = Date.now() + 3 * 60 * 1000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 1500));
    if (isSettled()) return;
    let res;
    try {
      res = await apiRequest(`/api/auth/sso/login-result/${code}`);
    } catch {
      continue; // vorübergehender Netzwerkfehler → weiter pollen
    }
    if (res.status === 'done') { finish(res); return; }
  }
  if (!isSettled()) finish(null); // Timeout → generic error, Popup wird geschlossen
}
